"use client";
import {CSSProperties, JSX, RefObject, useEffect, useRef, useState} from "react";
import styles from "@@/styles/components/shared/Kpis.module.scss";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {exo2Bold} from "@@/utils/fonts";

gsap.registerPlugin(ScrollTrigger);

interface Props {
    kpis: {
        kpi: {
            prefix?: string;
            number: number;
            suffix?: string;
        }
        label: string;
        animation?: {
            ref: RefObject<HTMLLIElement | null>;
            className: string;
            style: CSSProperties;
        }[];
    }[];
    drawLogo?: boolean;
    sectionId?: string;
    className?: string;
}

export default function Kpis({ kpis, sectionId, className, drawLogo }: Props): JSX.Element {

    const sectionRef = useRef<HTMLElement | null>(null);

    const animatedBg = useAppearOnScroll<HTMLElement>({from: "fadeIn", duration: 800, delay: 0, offset: 50});

    const pathRef = useRef<SVGPathElement | null>(null);

    const animatedItems = Array.isArray(kpis)
        ? kpis.map((item, index) => {
            const animation = useAppearOnScroll<HTMLLIElement>({
                from: "fromBottom",
                duration: 500,
                delay: index * 100,
                offset: 200,
            });

            return {
                ...item,
                animation,
                animatedValue: 0,
            };
        })
        : [];

    useEffect(() => {
        if (!pathRef.current) return;

        const pathLength = pathRef.current.getTotalLength();
        gsap.set(pathRef.current, {
            strokeDasharray: pathLength,
            strokeDashoffset: pathLength,
        });

        gsap.to(pathRef.current, {
            strokeDashoffset: 0,
            ease: "true",
            scrollTrigger: {
                trigger: sectionRef.current,
                start: "top-=200 bottom",
                end: "bottom-=50 top",
                scrub: true,
            },
        });
    }, []);


    return (
        <section id={sectionId} ref={sectionRef} className={`px-11 mb-10 position-relative color-dark overflow-hidden ${className}`}>
            <span ref={animatedBg.ref} className={`${animatedBg.className} gradient-bg`} style={animatedBg.style}></span>
            <span className={styles.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" id="Calque_1" version="1.1" viewBox="0 0 809 567">
                    <path
                        ref={pathRef}
                        className={styles.logoStroke}
                        d="M1075.32-415.19l-410.06,814.83c-5.42,10.78-20.51,11.59-27.05,1.45l-111.62-172.88c-.07-.1-.21-.1-.28,0l-97.42,151.91c-.26.42-.88.42-1.14,0l-137.44-205.31c-8.24-12.06-26.39-10.89-33.01,2.14L-127.01,933.66"
                    />
                </svg>
            </span>
            <div className="w-100 p-5 d-flex flex-column gap-5 rounded-1 overflow-hidden">
                <ul
                    className={`${styles.kpiListContainer}`}
                >
                    {animatedItems.map((item, index) => (
                        <li
                            key={index}
                            ref={item.animation.ref}
                            className={`duration-500 ease-in-out mx-height-100 ${styles.kpiContainer} ${item.animation.className}`}
                            style={item.animation.style}
                        >
                            <h3 className={`d-flex gap-1 ${styles.kpiContent} ${exo2Bold.className}`}>
                                {item.kpi.prefix && (<span>{item.kpi.prefix}</span>)}
                                <AnimatedNumber value={item.kpi.number} active={item.animation.className.includes("animate")} />
                                {item.kpi.suffix && (<span className={styles.suffix}>{item.kpi.suffix}</span>)}
                            </h3>
                            <p className={`${styles.kpiLabel}`}>{item.label}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}
function AnimatedNumber({ value, active }: { value: number; active: boolean }) {
    const [displayed, setDisplayed] = useState(0);
    const startTime = useRef<number | null>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!active || hasAnimated.current) return;

        const duration = 1000;
        hasAnimated.current = true;

        const step = (timestamp: number) => {
            if (!startTime.current) startTime.current = timestamp;
            const progress = Math.min((timestamp - startTime.current) / duration, 1);
            setDisplayed(Math.round(progress * value));
            if (progress < 1) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    }, [active, value]);

    return <span>{displayed}</span>;
}